import { useState, useCallback } from 'react';
import { WorkflowDefinition } from '../../types/workflows';

export interface CanvasNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: any;
}

export interface CanvasEdge {
  id: string;
  source: string;
  target: string;
  condition?: any;
}

interface UseWorkflowOperationsProps {
  workflow: WorkflowDefinition;
  onChange: (workflow: WorkflowDefinition) => void;
}

export const useWorkflowOperations = ({ workflow, onChange }: UseWorkflowOperationsProps) => {
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  const updateWorkflow = useCallback((nodes: CanvasNode[], edges: CanvasEdge[]) => {
    // Filter out edges from start node when saving to backend
    const backendEdges = edges.filter(edge => edge.source !== '-1');
    
    const updatedWorkflow: WorkflowDefinition = {
      ...workflow,
      graph: {
        nodes: nodes.map(node => ({
          node_id: node.id,
          name: node.data.name,
          config: {
            activity_name: node.type,
            parameters: node.data.config?.parameters || {},
            // Store position in config for persistence
            position: node.position
          }
        })),
        edges: backendEdges.map(edge => ({
          source_node_id: edge.source,
          destination_node_id: edge.target,
          condition: edge.condition || { type: 'always' }
        }))
      }
    };
    onChange(updatedWorkflow);
  }, [workflow, onChange]);

  const handleNodeDrag = useCallback((nodeId: string, newPosition: { x: number; y: number }, nodes: CanvasNode[]) => {
    const updatedNodes = nodes.map(node => 
      node.id === nodeId ? { ...node, position: newPosition } : node
    );
    // Note: Don't update workflow on every drag movement for performance
    // The canvas will handle batched updates
    return updatedNodes;
  }, []);

  const handleNodeDragEnd = useCallback((nodes: CanvasNode[], edges: CanvasEdge[]) => {
    // Update workflow when drag ends to persist positions
    updateWorkflow(nodes, edges);
  }, [updateWorkflow]);

  const handleNodeAdd = useCallback((nodeType: string, nodes: CanvasNode[], edges: CanvasEdge[], position?: { x: number; y: number }) => {
    const newNodeId = `node-${Date.now()}`;
    const newPosition = position || {
      x: 350,
      y: 80 + (nodes.length * 200)
    };
    
    const newNode: CanvasNode = {
      id: newNodeId,
      type: nodeType,
      position: newPosition,
      data: {
        name: nodeType.replace('Activity', '').replace(/([A-Z])/g, ' $1').trim(),
        config: { parameters: {} }
      }
    };
    
    const updatedNodes = [...nodes, newNode];
    updateWorkflow(updatedNodes, edges);
    return updatedNodes;
  }, [updateWorkflow]);

  const handleNodeAddAndConnect = useCallback((nodeType: string, sourceNodeId: string, condition: any, nodes: CanvasNode[], edges: CanvasEdge[], position?: { x: number; y: number }) => {
    const newNodeId = `node-${Date.now()}`;
    
    // Position the new node relative to the source node
    const sourceNode = nodes.find(n => n.id === sourceNodeId);
    const newPosition = position || {
      x: sourceNode ? sourceNode.position.x + 250 : 350,
      y: sourceNode ? sourceNode.position.y + 150 : 80 + (nodes.length * 200)
    };
    
    const newNode: CanvasNode = {
      id: newNodeId,
      type: nodeType,
      position: newPosition,
      data: {
        name: nodeType.replace('Activity', '').replace(/([A-Z])/g, ' $1').trim(),
        config: { parameters: {} }
      }
    };
    
    // Create edge connecting source to new node
    const newEdge: CanvasEdge = {
      id: `${condition.type}-${sourceNodeId}-${newNodeId}`,
      source: sourceNodeId,
      target: newNodeId,
      condition: condition || { type: 'always' }
    };
    
    const updatedNodes = [...nodes, newNode];
    const updatedEdges = edges.filter(edge => 
      !(edge.source === sourceNodeId && edge.condition?.type === condition?.type)
    );
    const existingEdge = edges.find(edge => 
      edge.source === newEdge.source &&
      edge.target === newEdge.target &&
      edge.condition?.type === newEdge.condition?.type
    );
    if (!existingEdge) {
      updatedEdges.push(newEdge);
    }
    updateWorkflow(updatedNodes, updatedEdges);
    return { nodes: updatedNodes, edges: updatedEdges, newNodeId };
  }, [updateWorkflow]);

  const handleNodeDelete = useCallback((nodeId: string, nodes: CanvasNode[], edges: CanvasEdge[]) => {
    const updatedNodes = nodes.filter(node => node.id !== nodeId);
    const updatedEdges = edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId);
    
    updateWorkflow(updatedNodes, updatedEdges);
    setSelectedNode(null);
    return { nodes: updatedNodes, edges: updatedEdges };
  }, [updateWorkflow]);

  const handleBackgroundClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  return {
    selectedNode,
    setSelectedNode,
    handleNodeDrag,
    handleNodeDragEnd,
    handleNodeAdd,
    handleNodeAddAndConnect,
    handleNodeDelete,
    handleBackgroundClick
  };
};